import { FeatureFlag } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { qs } from 'url-parse';

import { CrmZoneContext } from '@swf/components/CrmZone/CrmZone';
import CrmIframe from '@swf/components/CrmZone/components/CrmIframe/CrmIframe';
import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import { Message } from '@swf/sports/components/Message/Message';

import { TXTWCL_UnfortunatelyX_there_are_no_pr_ELL } from './SportsPromotionGridWidget.messages.json';
import styles from './SportsPromotionGridWidget.scss';
import Header from './components/Header/Header';
import IcePromotion from './components/IcePromotion/IcePromotion';
import type { PromotionTab } from './components/IcePromotion/types';
import Tabs from './components/Tabs/Tabs';
import { sportsPromotionGridWidgetModel, useSportsPromotionGridWidget } from './model';

// eslint-disable-next-line import/order
import Promotion from '@swf/sports/components/Promotion/Promotion';

const SportsPromotionGridWidget: WidgetFC = () => {
  const { widgetConfig, widgetSelect } = useSportsPromotionGridWidget();
  const ice = useSelector(widgetSelect.ice);
  const iceError = useSelector(widgetSelect.hasIceErrored);
  const crm = useSelector(widgetSelect.crm);
  const hasCrmRequestComplete = useSelector(widgetSelect.hasCrmRequestComplete);
  const hasCrmAndIceErrored = useSelector(widgetSelect.hasCrmAndIceErrored);
  const isLoading = useSelector(widgetSelect.isLoading);
  const isClientAuthenticated = useSelector(select.session.isClientAuthenticated);
  const hasClientSession = useSelector(select.session.hasClientSession);
  const isAuthCheckComplete = useSelector(select.session.isAuthenticationCheckComplete);

  const isIcePromotionsEnabled = useFeature(FeatureFlag.ISPICEPromotions);
  const history = useHistory();
  const location = useLocation();
  const { tab = 'LIVE' } = useMemo(
    () => qs.parse(location.search) as { tab?: PromotionTab },
    [location]
  );

  useEffect(() => {
    if (tab === 'COMPLETED' && hasClientSession && !isClientAuthenticated) {
      const search = new URLSearchParams(history.location.search);
      search.delete('tab');

      history.replace({ pathname: history.location.pathname, search: search.toString() });
    }
  }, [hasClientSession, history, isClientAuthenticated, tab]);

  const iceRequestComplete = isClientAuthenticated ? Boolean(ice?.[tab] || iceError) : true;
  const showPromotions = Boolean(
    hasCrmRequestComplete && iceRequestComplete && !hasCrmAndIceErrored
  );

  if (!isLoading && isIcePromotionsEnabled && !showPromotions && isAuthCheckComplete) {
    return <Message message={<Translation {...TXTWCL_UnfortunatelyX_there_are_no_pr_ELL} />} />;
  }

  const hasIcePromotions = ice && ice[tab].length;
  const hasCrmPromotions = crm && crm.length;
  const hasPromotions = hasIcePromotions || hasCrmPromotions;

  return (
    <div>
      {!widgetConfig.hideHeader ? <Header /> : null}
      {!widgetConfig.hideTabs ? <Tabs /> : null}

      {!isLoading && !hasPromotions ? (
        <Message message={<Translation {...TXTWCL_UnfortunatelyX_there_are_no_pr_ELL} />} />
      ) : null}

      {hasPromotions ? (
        <div className={styles.wrapper}>
          {hasIcePromotions
            ? ice[tab].map((promotion) => (
                <Promotion key={promotion.promoId}>
                  <IcePromotion promotion={promotion} />
                </Promotion>
              ))
            : null}

          {tab === 'LIVE' && hasCrmPromotions
            ? crm.map((content, i) => (
                <Promotion key={`${content}-${i}`}>
                  <CrmZoneContext.Provider value="ISPPROMOTIONS">
                    <CrmIframe html={content} dynamicHeight data-testid="crm-sports-promotion" />
                  </CrmZoneContext.Provider>
                </Promotion>
              ))
            : null}
        </div>
      ) : null}
    </div>
  );
};

SportsPromotionGridWidget.model = sportsPromotionGridWidgetModel;

export default withWidgetTestId(SportsPromotionGridWidget, 'sports-promotion-grid');
